import store from '@/store'
import {
  connectionService,
  GEOCODING_REVERSE_URI,
  MIN_PAGINATION_VALUE,
  MIN_SPEED_THING_VEHICLE,
  RACE_TIMEOUT,
  THING_TRAIL_SUMMARY_URI,
  THING_TRAIL_URI
} from '@leapsight/styleguide'
import { i18n } from '@leapsight/portal'
import { GlobalHelpers, ThingsHelpers, TrailsHelpers } from '../mainExports'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

let promiseRaceTimeout

const eventsHash = {
  'mrn:event:speed_limit_breached': signal =>
    `${signal.data.agent.conditions['mrn:agent_condition:speed_limit'].speed_limit}Km/h`,
  'mrn:event:geofence_ingress': signal =>
    signal.data.agent.conditions['mrn:agent_condition:geofence'].geofences.data
      .properties.name,
  'mrn:event:geofence_egress': signal =>
    signal.data.agent.conditions['mrn:agent_condition:geofence'].geofences.data
      .properties.name,
  'mrn:event:speed_limit:geofence_ingress_breached': signal =>
    `${signal.data.agent.conditions['mrn:agent_condition:speed_limit'].speed_limit}Km/h,
    ${signal.data.agent.conditions['mrn:agent_condition:geofence'].geofences.data.properties.name}`,
  'mrn:event:contact_on:geofence_in': signal =>
    signal.data.agent.conditions['mrn:agent_condition:geofence'].geofences.data
      .properties.name,
  'mrn:event:main_power_on:geofence_in': signal =>
    signal.data.agent.conditions['mrn:agent_condition:geofence'].geofences.data
      .properties.name,
  'mrn:event:main_power_off:geofence_outside_all': signal => '',
  'mrn:event:main_power_off': signal =>
    !signal.data.agent.conditions['mrn:agent_condition:main_power']
      .main_power_on
      ? 'Desconexión de batería'
      : null,
  'mrn:event:movement_start': signal =>
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    `Se activa en ${convertSectoDay(
      signal.data.agent.conditions['mrn:agent_condition:movement_start']
        .minimum_in_movement_time
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
    )}, Parada maxima ${convertSectoDay(
      signal.data.agent.conditions['mrn:agent_condition:movement_start']
        .minimum_stop_time
    )}`,
  'mrn:event:movement_start:geofence_ingress_breached': signal =>
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    `${convertSectoDay(signal.data.duration)}, ${signal.data.max_speed}Km/s, ${
      signal.data.agent.conditions['mrn:agent_condition:geofence'].geofences
        .data.properties.name
    }`,
  'mrn:event:stopped': signal => '',
  'mrn:event:panic': signal => ''
}

const getMaxSpeedSignalId = data => {
  let velMax = 0

  data.forEach(element => {
    if (velMax < element.speed) velMax = element.speed
  })

  return data.filter(signal => velMax === signal.speed)[0].id
}

const timeoutFunction = () => {
  clearTimeout(promiseRaceTimeout)
  return new Promise(resolve => {
    return setTimeout(resolve, RACE_TIMEOUT, [])
  })
}

const functionRace = (from, to, page, partialLocations) => {
  return new Promise(resolve => {
    const locations = partialLocations
    const thingId = store.getters['trailState/getThing'].id
    const hasServiceTelemetry =
      store.getters['trailState/hasThingServiceTelemery']
    const connection = GlobalHelpers.getConnection()
    const url = THING_TRAIL_URI
    const args = [thingId]
    const security = connectionService.createkwArgs()
    const options = {
      timeout: 300000
    }
    const kwArgs = {
      ...security,
      query_spec: {
        _page: page,
        _limit: MIN_PAGINATION_VALUE,
        _sort: 'event_timestamp,seq_num+asc',
        _from: from,
        _to: to,
        _telemetry: hasServiceTelemetry
      }
    }
    connection.session
      .call(url, args, kwArgs, options)
      .then(
        res => {
          res.map(item => {
            locations.push(item)
          })
          resolve(locations)
        },
        wampError => {
          store.dispatch('uiState/openSnackBar', {
            msg: 'No se pudo recuperar información.'
          })
          store.dispatch('trailState/setDataLoading', false)
          store.commit('trailState/setHideGreeting', { hideGreeting: false })
          console.error(wampError)
        }
      )
      .catch(err => {
        console.error(err)
      })
  })
}

/**
 * Devuelve las columnas para la segunda hoja del excel a exportar,
 * la cual esta apuntada a mostrar todos los puntos del recorrido
 * @returns
 */
const getLocationsColumns = (withEvents = false, withTemperature = false) => {
  const baseColumns = [
    {
      header: i18n.t('live_tracking.map.trail_grid.export_excel.nro_orden'),
      key: 'id'
    },
    {
      header: i18n.t('live_tracking.map.trail_grid.export_excel.date_time_gps'),
      key: 'date'
    },
    {
      header: i18n.t('live_tracking.map.trail_grid.export_excel.speed'),
      key: 'speed'
    },
    {
      header: i18n.t('live_tracking.map.trail_grid.export_excel.heading'),
      key: 'heading'
    },
    {
      header: i18n.t('live_tracking.map.trail_grid.export_excel.location'),
      key: 'location'
    },
    {
      header: i18n.t('live_tracking.map.trail_grid.export_excel.latitude'),
      key: 'latitude'
    },
    {
      header: i18n.t('live_tracking.map.trail_grid.export_excel.longitude'),
      key: 'longitude'
    },
    {
      header: `${i18n.t(
        'live_tracking.map.trail_grid.export_excel.partial_distance'
      )} (${i18n.t(
        'live_tracking.map.trail_grid.labels.data_table_partial_distance_unit'
      )})`,
      key: 'partialDistance'
    },
    {
      header: i18n.t('live_tracking.map.trail_grid.export_excel.contact'),
      key: 'contact'
    },
    {
      header: i18n.t('live_tracking.map.trail_grid.export_excel.status'),
      key: 'status'
    }
  ]
  if ('mrn:service:ibutton' in store.getters['trailState/getThing']?.services) {
    baseColumns.push({
      header: 'Conductor',
      key: 'driver'
    })
  }
  if (
    'mrn:service:telemetry' in store.getters['trailState/getThing']?.services
  ) {
    baseColumns.push({
      header:
        i18n.t('live_tracking.map.trail_grid.export_excel.fuel_consumption') +
        ' (lts)',
      key: 'partialOdoliter'
    })
  }
  if (
    'mrn:service:telemetry' in store.getters['trailState/getThing']?.services
  ) {
    baseColumns.push({
      header:
        i18n.t('live_tracking.map.trail_grid.export_excel.fuel_tank') + ' (%)',
      key: 'fuelTank'
    })
  }
  if (withTemperature) {
    baseColumns.push({
      header: i18n.t('live_tracking.map.summary.labels.label_temperature'),
      key: 'temperature'
    })
  }
  if (withEvents) {
    baseColumns.push({
      header: i18n.t('live_tracking.map.trail_grid.export_excel.events'),
      key: 'events'
    })
  }
  return baseColumns
}

const thingTrailSummaryRace = (from, to) => {
  return new Promise((resolve, reject) => {
    const thingId = store.getters['trailState/getThing'].id
    const hasServiceTelemetry =
      store.getters['trailState/hasThingServiceTelemery']
    const connection = GlobalHelpers.getConnection()
    const url = THING_TRAIL_SUMMARY_URI
    const args = [thingId]
    const security = connectionService.createkwArgs()
    const kwArgs = {
      ...security,
      query_spec: {
        _from: from,
        _to: to
      },
      query_match: {
        type: 'valid',
        telemetry: hasServiceTelemetry
      }
    }

    connection.session
      .call(url, args, kwArgs, {})
      .then(
        res => {
          resolve(res)
        },
        wampError => {
          store.dispatch('uiState/openSnackBar', {
            msg: 'No se pudo recuperar información.'
          })
          store.dispatch('trailState/setDataLoading', false)
          console.error(wampError)
        }
      )
      .catch(err => {
        reject(err)
        console.error(err)
      })
  })
}

const getThingTrailSummary = (from, to) => {
  return new Promise(resolve => {
    const p1 = timeoutFunction()
    const p2 = thingTrailSummaryRace(from, to)

    Promise.race([p1, p2])
      .then(value => {
        resolve(value)
      })
      .catch(error => {
        resolve(error)
      })
  })
}

const getTrailLocations = (from, to, page = 1, partialLocations = []) => {
  return new Promise(resolve => {
    const p1 = timeoutFunction()
    // Se utiliza functionRace para evitar en la recursión llamar al Promise.race n veces

    const p2 = functionRace(from, to, page, partialLocations)

    p2.then(value => resolve(value)).catch(err => resolve(err))
    // Promise.race([p1, p2])
    //   .then(value => {
    //     resolve(value)
    //   })
    //   .catch(error => {
    //     resolve(error)
    //   })
  })
}

const getTrailLocation = location => {
  return new Promise((resolve, reject) => {
    const connection = store.getters['authState/connection']
    const args = []
    const kwArgs = connectionService.createkwArgs()
    const options = {}

    const latitude = location.coordinates[0]
    const longitude = location.coordinates[1]
    const index = 1
    const geoLocationThing = { index, latitude, longitude }
    args.push(geoLocationThing)
    connection.session
      .call(GEOCODING_REVERSE_URI, args, kwArgs, options)
      .then(
        res => {
          resolve(res)
        },
        wampError => {
          console.log(wampError)
          reject(wampError)
        }
      )
      .catch(err => {
        console.log(err)
      })
  })
}

const getBundleLocation = bundle => {
  return new Promise((resolve, reject) => {
    const connection = store.getters['authState/connection']
    const kwArgs = connectionService.createkwArgs()
    const options = {}

    connection.session
      .call(GEOCODING_REVERSE_URI, bundle, kwArgs, options)
      .then(
        res => {
          resolve(res)
        },
        wampError => {
          console.log(wampError)
          reject(wampError)
        }
      )
      .catch(err => {
        console.log(err)
      })
  })
}

const convertMetersToKm = meters => {
  return (meters / 1000).toFixed(1)
}

const convertSectoDay = n => {
  const day = parseInt(n / (24 * 3600))

  n = n % (24 * 3600)
  const hour = parseInt(n / 3600)

  n %= 3600
  const minutes = n / 60

  n %= 60
  const seconds = n

  if (day > 0)
    return (
      day +
      'd ' +
      hour +
      'h ' +
      minutes.toFixed() +
      'm ' +
      seconds.toFixed() +
      's'
    )

  if (hour > 0)
    return hour + 'h ' + minutes.toFixed() + 'm ' + seconds.toFixed() + 's'

  return minutes.toFixed() + 'm ' + seconds.toFixed() + 's'
}

const elapsedTime = timestamp => {
  let difference = timestamp
  const days = Math.floor(difference / 1000 / 60 / 60 / 24)
  difference -= days * 1000 * 60 * 60 * 24
  const hours = Math.floor(difference / 1000 / 60 / 60)
  difference -= hours * 1000 * 60 * 60
  const minutes = Math.floor(difference / 1000 / 60)
  difference -= minutes * 1000 * 60
  const seconds = Math.floor(difference / 1000)
  if (days > 0) {
    const elapsedTime =
      days + 'd ' + hours + 'h ' + minutes + 'm ' + seconds + 's'
    return elapsedTime
  }
  if (hours > 0) {
    const elapsedTime = hours + 'h ' + minutes + 'm ' + seconds + 's'
    return elapsedTime
  } else {
    const elapsedTime = minutes + 'm ' + seconds + 's'
    return elapsedTime
  }
}

const transformStringToJSON = inputString => {
  // Reemplaza las comas que están dentro de las llaves por un carácter especial para dividir
  const stringWithoutCommas = inputString.replace(
    /\{([^{}]+)\}/g,
    (match, group) => {
      return `{${group.replace(/,/g, '|')}}`
    }
  )

  // Reemplaza los dos puntos por comas y los caracteres especiales nuevamente por comas
  const jsonString = stringWithoutCommas
    .replace(/(mrn:geofence:[A-Za-z0-9-]+)/g, '"$1"')
    .replace(/([a-zA-Z0-9_:]+)=/g, '"$1":')
    .replace('=', ':')
    .replace(/([A-Za-z ]+)([}])/g, '"$1"}')
    .replace(/\|/g, ',')

  // Parsea la cadena JSON en un objeto JavaScript
  let resultObject = {}
  try {
    resultObject = JSON.parse(jsonString)
  } catch (e) {
    resultObject = {}
  }

  return resultObject
}

/**
 * @desc Genera el string que sera insertado en una celda del excel a exportar
 * @param {array} events Array de eventos
 */
const processEventsForExcel = events => {
  const conditions = []
  for (let index = 0; index < events.length; index++) {
    const element = events[index]
    const agentWindow = GlobalHelpers.parseWindow(element.data.agent.window)
    const agentLabel = element.data.agent.label

    let result = ''
    let agentCondition = element.data.agent.conditions

    if (typeof agentCondition === 'string') {
      element.data.agent.conditions = transformStringToJSON(agentCondition)
      agentCondition = element.data.agent.conditions
    }

    if (typeof agentCondition === 'object') {
      const keyConditions = Object.keys(agentCondition)
      if (
        keyConditions.some(key => key.match(/(mrn:agent_condition:[A-Za-z]+)/g))
      ) {
        result =
          element.type in eventsHash ? eventsHash[element.type](element) : ''
      }
    }

    conditions.push(`${agentLabel}, ${result}, ${agentWindow}`)
  }
  const strToReturn = conditions.join(' || ')
  return strToReturn
}

// Conversion de metros a kilometros
const mtsToKms = distance => {
  const partialDistance = distance / 1000
  return partialDistance.toFixed(1)
}

const getThingInfoExcel = data => {
  return [
    [
      i18n.t('live_tracking.map.trail_grid.export_excel.date_range'),
      data.dateRange
    ],
    [
      i18n.t('live_tracking.map.trail_grid.export_excel.thing_name'),
      data.thingName
    ],
    [i18n.t('live_tracking.map.trail_grid.export_excel.domain'), data.domain],
    [
      i18n.t('live_tracking.map.trail_grid.export_excel.local_time'),
      data.localRequestDate
    ]
  ]
}

const getTitlesSummaryExcel = () => {
  const titlesSumaryExcel = [
    [
      i18n.t('live_tracking.map.summary.labels.label_max_speed'),
      `${store.getters['trailState/getThingTrailSummary'].maxSpeed} ${i18n.t(
        'live_tracking.map.summary.labels.label_max_speed_unit'
      )}`
    ],
    [
      i18n.t('live_tracking.map.summary.labels.label_max_temperature'),
      `${
        store.getters['trailState/getThingTrailSummary'].maxTemperatureFormatted
      } ${i18n.t('live_tracking.map.summary.labels.label_temperature_unit')}`
    ],
    [
      i18n.t('live_tracking.map.summary.labels.label_length'),
      `${parseFloat(
        store.getters['trailState/getThingTrailSummary'].distanceFormatted
      ).toFixed(1)} Km`
    ],
    [
      i18n.t('live_tracking.map.summary.labels.label_fuel_consumption'),
      `${parseFloat(
        store.getters['trailState/getThingTrailSummary']
          .fuelConsumptionFormatted
      ).toFixed(1)} lts`
    ],
    [
      i18n.t('live_tracking.map.summary.labels.label_contact_off_time'),
      store.getters['trailState/getThingTrailSummary']
        .timeWithContactOffFormatted
    ],
    [
      i18n.t('live_tracking.map.summary.labels.label_contact_on_time_moving'),
      store.getters['trailState/getThingTrailSummary'].timeInMovementFormatted
    ],
    [
      i18n.t('live_tracking.map.summary.labels.label_contact_on_time_still'),
      store.getters['trailState/getThingTrailSummary'].timeStoppedFormatted
    ],
    [
      i18n.t('live_tracking.map.summary.labels.label_contact_on_time'),
      store.getters['trailState/getThingTrailSummary'].timeWithContactFormatted
    ]
  ]
  if (Boolean(store.getters['trailState/getThing'].telemetry)) {
    titlesSumaryExcel.push(
      [
        i18n.t('live_tracking.map.summary.labels.label_start_value_fuel_tank'),
        `${store.getters['trailState/getFirstFuelTankNonNull']}${i18n.t(
          'live_tracking.map.summary.labels.label_fuel_tank_unit'
        )}`
      ],
      [
        i18n.t('live_tracking.map.summary.labels.label_last_value_fuel_tank'),
        `${store.getters['trailState/getLastFuelTankNonNull']}${i18n.t(
          'live_tracking.map.summary.labels.label_fuel_tank_unit'
        )}`
      ]
    )
  }
  return titlesSumaryExcel
}

const getOdometerByCase = obj => {
  let odometerByCase = {}
  const hasServiceTelemetry =
    store.getters['trailState/hasThingServiceTelemery']
  if (hasServiceTelemetry) {
    odometerByCase = obj.candata?.odometer
  } else {
    odometerByCase = obj.odometer
  }
  return odometerByCase ?? 0
}

const getStatus = (item, translateStatus = false) => {
  const type = store.getters['trailState/getThing'].type
  let status = ThingsHelpers.getThingStatus(type, item)
  if (translateStatus)
    switch (status) {
      case 'moving':
        status = i18n.t(
          'live_tracking.map.thing_info.labels.label_thing_status_moving'
        )
        break
      case 'on':
        status = i18n.t(
          'live_tracking.map.thing_info.labels.label_thing_status_on'
        )
        break
      case 'stop':
        status = i18n.t(
          'live_tracking.map.thing_info.labels.label_thing_status_stop'
        )
        break
      case 'tow':
        status = i18n.t(
          'live_tracking.map.thing_info.labels.label_thing_status_tow'
        )
        break
    }
  return status
}

const getPartialDistance = distance => {
  const hasServiceTelemetry =
    store.getters['trailState/hasThingServiceTelemery']
  return hasServiceTelemetry
    ? distance.toFixed(1)
    : TrailsHelpers.mtsToKms(distance)
}

const getHeading = item => {
  if (item.speed >= MIN_SPEED_THING_VEHICLE) {
    const enHeading = ThingsHelpers.getThingHeading(item.heading)
    const esHeading = i18n.t(`general.labels.heading.short.${enHeading}`)
    return esHeading
  } else {
    return '-'
  }
}

const getLocationDate = item => {
  dayjs.extend(utc)
  dayjs.extend(timezone)
  const now = dayjs
    .utc(item.timestamp)
    .tz(localStorage.getItem('timeZone'))
    .format('DD MMM YYYY H:mm:ss')
  return now
}

function processTemperatureValue(parameter) {
  let result = '-'
  const thingInfo = store.getters['uiState/thingInfo']
  let hasMultiTemperatureService = false
  
  if (thingInfo.hasOwnProperty('services')) {
    hasMultiTemperatureService = Object.keys(
      store.getters['uiState/thingInfo']['services']
    ).find(e => e === 'mrn:service:temperature_multi_sensor')
  } else {
    hasMultiTemperatureService = store.getters.getThingServices.find(e => e === 'mrn:service:temperature_multi_sensor')
  }

  if (hasMultiTemperatureService) {
    if (parameter.sensors !== null) {
      for (const sensor in parameter.sensors) {
        if (
          parameter.sensors[sensor] &&
          sensor.substring(0, 11) === 'temperature'
        ) {
          const sensorIndex =
            sensor.substring(11) === '' ? '0' : sensor.substring(11)
          result = `T${sensorIndex}: ${parameter.sensors[sensor].toFixed(1)} °C`
        }
      }
    }
  } else {
    if (parameter.temperature && parameter.temperature2) {
      result =
        'T1: ' +
        parameter.temperature.toFixed(1) +
        i18n.t('live_tracking.map.summary.labels.label_temperature_unit') +
        ' / ' +
        'T2: ' +
        parameter.temperature2.toFixed(1) +
        i18n.t('live_tracking.map.summary.labels.label_temperature_unit')
    } else if (parameter.temperature) {
      result =
        'T1: ' +
        parameter.temperature.toFixed(1) +
        i18n.t('live_tracking.map.summary.labels.label_temperature_unit')
    } else if (parameter.temperature2) {
      result =
        'T2: ' +
        parameter.temperature2.toFixed(1) +
        i18n.t('live_tracking.map.summary.labels.label_temperature_unit')
    }
  }
  return result
}

const processRowsForExcel = (
  items,
  hasEvents = false,
  showTemperature = false,
  hasMultiTemperatureService = false,
  isCanbus = false
) => {
  const rowsForExcel = [] // Array de datos a devolver para exportación
  const arrToResolveGeoreference = [] // Array con elementos sin ubicación resuelta
  let location
  for (let index = 0; index < items.length; index++) {
    const element = items[index]
    //Se construye la estructura del objeto salvo la ubicación que se añade solo si se encuentra resuelta la georeferencia
    const { id, speed } = element
    const date = getLocationDate(element)
    const heading = getHeading(element)
    const latitude = element.location.coordinates[0]
    const longitude = element.location.coordinates[1]
    const partialDistance = getPartialDistance(element.partialDistance)
    const partialOdoliter = isCanbus
      ? element.partialOdoliter?.toFixed(1)
        ? element.partialOdoliter?.toFixed(1)
        : '-'
      : null
    const fuelTank = isCanbus
      ? Boolean(element.fuelTank)
        ? element.fuelTank
        : '-'
      : null
    const driver = element.driver?.column
    const temperature = processTemperatureValue(element)
    const isContactOn = element.thing_contact_on ?? element.ignition_status
    const contact = Boolean(isContactOn) ? 'On' : 'Off'
    const status = getStatus(element, true) //true solicita devolver traducido el status
    let events
    if (element.events_data !== null)
      events = processEventsForExcel(element.events_data)
    else events = ''
    if (element.hasOwnProperty('geoReference')) {
      location = element.geoReference.formatted_address
    } else {
      // Si no se encuentra resuelta la georeferencia se guarda el indice del item, su latitud y longitud en el array para posterior proceso
      location = ''
      if (showTemperature || (!showTemperature && (!hasMultiTemperatureService || temperature === '-'))) {
        arrToResolveGeoreference.push({
          index,
          latitude,
          longitude
        })
      }
    }
    const rows = {
      contact,
      date,
      ...(hasEvents && { events }),
      heading,
      id,
      latitude,
      location,
      longitude,
      partialDistance,
      driver,
      ...(isCanbus && { partialOdoliter }),
      ...((showTemperature ||
        (hasMultiTemperatureService && temperature === '-')) && {
        temperature
      }),
      ...(isCanbus && { fuelTank }),
      speed,
      status
    }
    //Array parcial con objetos a exportar
    if (
      showTemperature ||
      (!showTemperature && (!hasMultiTemperatureService || temperature === '-'))
    ) {
      rowsForExcel.push(rows)
    }
  }
  return { rowsForExcel, arrToResolveGeoreference }
}

export default {
  getOdometerByCase,
  getMaxSpeedSignalId,
  functionRace,
  getTitlesSummaryExcel,
  timeoutFunction,
  getThingInfoExcel,
  getLocationsColumns,
  thingTrailSummaryRace,
  getThingTrailSummary,
  getTrailLocations,
  getTrailLocation,
  getBundleLocation,
  convertMetersToKm,
  convertSectoDay,
  elapsedTime,
  processEventsForExcel,
  mtsToKms,
  processRowsForExcel,
  getLocationDate,
  getStatus,
  getHeading,
  getPartialDistance,
  processTemperatureValue
}
